<template>
  <v-dialog
    max-width="700"
    :model-value="isDialogOpen"
    @update:model-value="closeDialog"
  >
    <v-card
      class="pa-4"
    >
      <page-title :value="`献立表をインポート`" />

      <div class="my-4">
        <v-file-input
          truncate-length="15"
          accept=".xlsx"
          label="献立表を選択してください"
          placeholder="献立表を選択してください"
          persistent-placeholder
          v-model="files"
        ></v-file-input>
      </div>

      <v-progress-linear v-if="isImporting" indeterminate color="primary" class="my-4" />

      <div class="my-4 d-flex align-center">
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :disabled="isImporting || !hasFile"
          @click="startImport"
        >インポートを開始</v-btn>
        <v-spacer />
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { foodMenusModule } from '@/store/viewModules/food-menu/foodMenusModule';
import PageTitle from '@/components/atoms/PageTitle.vue';
import { classModule } from '@/store/dataModules/classModule';
import LocalDataService from '@/service/LocalDataService';
import ApiMeal from '@/api/ApiMeal';
import dayjs from 'dayjs';
import ApiResource from '@/api/ApiResource';

interface DataType {
  isImporting: boolean;
  files: File | File[] | null; // Vuetifyのバージョン違いによる単一・配列どちらにも対応
}

export default defineComponent({
  components: {
    PageTitle,
  },

  data: (): DataType => ({
    isImporting: false,
    files: null, // 初期値をnullに変更
  }),

  computed: {
    isDialogOpen() {
      return foodMenusModule.isImportDialogOpen;
    },
    year() {
      return dayjs(foodMenusModule.importYearMonth).year();
    },
    month() {
      return dayjs(foodMenusModule.importYearMonth).month() + 1;
    },
    // ファイルが選択されているかどうかの安全な判定用
    hasFile(): boolean {
      if (!this.files) return false;
      if (Array.isArray(this.files)) {
        return this.files.length > 0;
      }
      return this.files instanceof File;
    },
    // 実際にアップロードする単一のファイル（Fileオブジェクト）を取得
    selectedFile(): File | null {
      if (!this.files) return null;
      if (Array.isArray(this.files)) {
        return this.files.length > 0 ? this.files[0] : null;
      }
      return this.files instanceof File ? this.files : null;
    }
  },

  methods: {
    // Vuetify 3 の仕様変更に対応（@click:outside の代わり）
    closeDialog(value: boolean) {
      if (!value) {
        foodMenusModule.setIsImportDataDialogOpen(false);
        this.files = null; // ダイアログを閉じたときにファイルをクリア
      }
    },
    async startImport() {
      const nurseryId = LocalDataService.getNurseryId();
      const targetFile = this.selectedFile; // 確実にFileオブジェクトを取得

      if (nurseryId && targetFile) {
        this.isImporting = true;
        try {
          const uploadTargetResponse = await ApiMeal.getUploadUrl({
            nurseryId,
            year: this.year,
            month: this.month,
            filename: targetFile.name
          });
          
          if (uploadTargetResponse) {
            await ApiResource.uploadFile({
              url: uploadTargetResponse.url,
              file: targetFile, 
            });
            // 必要に応じて成功メッセージなどをここに追加
          }
        } catch (error) {
          console.error("アップロードに失敗しました", error);
          alert("アップロードに失敗しました。");
        } finally {
          this.isImporting = false;
          this.closeDialog(false); // 処理が終わったら確実にダイアログを閉じる
        }
      }
    }
  },

  mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      classModule.listClass({ nurseryId });
    }
  }
});
</script>